import * as THREE from 'three';
import { CLICK_SLOP_PX } from './picking';

/** Where a mouse rests over the canvas, and which presses are clicks rather than orbits or pans. */
export class PointerClicks {
  readonly ndc = new THREE.Vector2();
  private pressedAt: { x: number; y: number } | null = null;
  private clickable = false;
  private dragging = false;
  private inside = false;
  private stale = false;

  constructor(
    private readonly element: HTMLElement,
    private readonly isCovered: (event: PointerEvent) => boolean,
    private readonly canClick: () => boolean,
    private readonly onClick: (rect: DOMRect, event: PointerEvent) => void,
    private readonly onLeave: () => void,
  ) {
    element.addEventListener('pointermove', this.handleMove);
    element.addEventListener('pointerdown', this.handleDown);
    element.addEventListener('pointerup', this.handleUp);
    element.addEventListener('pointercancel', this.handleCancel);
    element.addEventListener('pointerleave', this.handleLeave);
  }

  get hovering(): boolean {
    return this.inside && !this.dragging;
  }

  /** The view moved under a still pointer. */
  invalidate(): void {
    if (this.inside) this.stale = true;
  }

  takeStale(): boolean {
    const stale = this.stale;
    this.stale = false;
    return stale;
  }

  cancelPress(): void {
    this.pressedAt = null;
  }

  dispose(): void {
    this.element.removeEventListener('pointermove', this.handleMove);
    this.element.removeEventListener('pointerdown', this.handleDown);
    this.element.removeEventListener('pointerup', this.handleUp);
    this.element.removeEventListener('pointercancel', this.handleCancel);
    this.element.removeEventListener('pointerleave', this.handleLeave);
  }

  private setFrom(event: PointerEvent): DOMRect | null {
    const rect = this.element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    this.ndc.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    return rect;
  }

  private movedFromPress(event: PointerEvent): boolean {
    const start = this.pressedAt;
    return start !== null && Math.hypot(event.clientX - start.x, event.clientY - start.y) > CLICK_SLOP_PX;
  }

  private readonly handleMove = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return;
    if (this.isCovered(event)) {
      this.handleLeave();
      return;
    }
    if (!this.setFrom(event)) return;
    this.inside = true;
    this.stale = true;
    if (this.movedFromPress(event)) this.dragging = true;
  };

  private readonly handleDown = (event: PointerEvent) => {
    this.pressedAt = { x: event.clientX, y: event.clientY };
    this.clickable = this.canClick() && event.button === 0 && !this.isCovered(event);
  };

  private readonly handleUp = (event: PointerEvent) => {
    const click = this.pressedAt !== null && this.clickable && !this.movedFromPress(event);
    this.pressedAt = null;
    this.dragging = false;
    this.invalidate();
    const rect = click ? this.setFrom(event) : null;
    if (rect) this.onClick(rect, event);
  };

  private readonly handleCancel = () => {
    this.pressedAt = null;
    this.dragging = false;
  };

  private readonly handleLeave = () => {
    this.inside = false;
    this.dragging = false;
    this.onLeave();
  };
}
